import React, { useState, Fragment } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";
import Card from "components/Card";
import Status from "components/Status";
import DataTable from "components/DataTable";
import { Spinner } from "components/Loader";
import { ActionButton, SecondaryButton } from "components/Buttons";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { Col, Row } from "components/Containers";
import { Link } from "components/Links";
import { SectionTitle } from "components/Layouts";
import { formatElapsedTime, normalizeWhitespace, shortenText } from "utils";
import { useAIRoleBuilderTasks, useAIRoleBuilderTasksContext } from "hooks";
import {
  AI_ROLE_BUILDER_TASK_DISPLAY_STATE as DISPLAY_STATE,
  AI_ROLE_BUILDER_TASKS_CONFIG,
  getRoleBuilderTaskDisplayState,
} from "constants/index";
import AIRoleBuilderTasksTitle from "./AIRoleBuilderTasksTitle";

/**
 * AIRoleBuilderTasksList
 *
 * Collapsible summary of the AI Role Builder queue
 *
 * @param {function} onReview    Called with a task when the user chooses "Review"
 * @param {boolean}  showHeader  Wrap the table in its own titled, collapsible Card.
 */
const AIRoleBuilderTasksList = ({ onReview, showHeader = true, ...props }) => {
  const { tasks, loading } = useAIRoleBuilderTasksContext();
  const { handleDiscard } = useAIRoleBuilderTasks();
  const [expanded, setExpanded] = useState(true);
  const intl = useIntl();

  return (
    <Card>
      {showHeader && (
        <StyledSectionTitle
          title={
            <Row gap="0.8rem">
              <Icon type={ICON_TYPE.bolt} size={ICON_SIZE.medium} color={colors.purpleRainBase} />
              <AIRoleBuilderTasksTitle />
            </Row>
          }
          actionBar={<span css={styles.summary}>{!loading && renderSummary(tasks)}</span>}
          isExpandable
          isExpanded={expanded}
          onExpand={setExpanded}
          noMargin
        />
      )}
      {(!showHeader || expanded) && (
        <DataTable
          data={tasks}
          columns={COLUMNS({ intl, onReview, onDiscard: handleDiscard })}
          loading={loading}
          emptyMessage={{
            title: "AIRoleBuilder.Tasks.Empty.Title",
            description: "AIRoleBuilder.Tasks.Empty.Description",
          }}
          {...props}
        />
      )}
    </Card>
  );
};

const StyledSectionTitle = styled(SectionTitle)`
  margin: 0 0 0.5rem 0;
`;

const COLUMNS = ({ intl, onReview, onDiscard }) => [
  {
    label: <FormattedMessage id="AIRoleBuilder.Tasks.Column.Role" />,
    cell: (task) => (
      <Col gap="0.4rem">
        <Row gap="0.8rem" noWrap>
          {getStateIndicator(getRoleBuilderTaskDisplayState(task))}
          <span>{taskDisplayName(task)}</span>
        </Row>
        {/* A PDF-only request has nothing else to show for what was asked. */}
        {task?.jobDescriptionFileName && (
          <Link href={task.jobDescriptionFileUrl} target="_blank" rel="noreferrer" css={styles.file}>
            <Icon type={ICON_TYPE.file} size={ICON_SIZE.xsmall} />
            {task.jobDescriptionFileName}
          </Link>
        )}
        {renderJobDescription(task)}
      </Col>
    ),
    width: "30%",
  },
  {
    label: <FormattedMessage id="AIRoleBuilder.Tasks.Column.RequestedBy" />,
    cell: (task) => <span css={styles.requested_by}>{task?.submittedByName || "—"}</span>,
    width: "15%",
  },
  {
    label: <FormattedMessage id="AIRoleBuilder.Tasks.Column.Status" />,
    cell: (task) => getStatusChip(getRoleBuilderTaskDisplayState(task)),
    width: "10%",
  },
  {
    label: <FormattedMessage id="AIRoleBuilder.Tasks.Column.Step" />,
    cell: (task) => <span css={styles.step}>{getStep(task)}</span>,
    width: "20%",
  },
  {
    label: <FormattedMessage id="AIRoleBuilder.Tasks.Column.Elapsed" />,
    cell: (task) => <span css={styles.step}>{formatElapsedTime(task?.submittedAt, task?.finishedAt) || "—"}</span>,
    width: "10%",
  },
  {
    // Deliberately unlabelled
    cell: (task) => (
      <Row gap="0.4rem" css={styles.actions}>
        {getRoleBuilderTaskDisplayState(task) === DISPLAY_STATE.READY && (
          <SecondaryButton onClick={() => onReview?.(task)}>
            <Icon type={ICON_TYPE.eye} size={ICON_SIZE.medium} />
            <FormattedMessage id="AIRoleBuilder.Tasks.Review" />
          </SecondaryButton>
        )}
        <ActionButton onClick={() => onDiscard(task)} title={intl.formatMessage({ id: "AIRoleBuilder.Tasks.Discard" })}>
          <Icon type={ICON_TYPE.trash} size={ICON_SIZE.large} color={colors.error} />
        </ActionButton>
      </Row>
    ),
    width: "15%",
  },
];

// The generated job title only exists once the agent finishes.
const taskDisplayName = (task) => {
  if (task?.label) return task.label;

  return <FormattedMessage id="AIRoleBuilder.Tasks.PendingName" values={{ organization: task?.companyName || "" }} />;
};

const renderJobDescription = (task) => {
  const text = normalizeWhitespace(task?.jobDescription);

  if (!text) return null;

  return (
    <span css={styles.description} title={task.jobDescription}>
      {shortenText(text, AI_ROLE_BUILDER_TASKS_CONFIG.DESCRIPTION_PREVIEW_LENGTH)}
    </span>
  );
};

const getStateIndicator = (displayState) => {
  if (displayState === DISPLAY_STATE.READY) {
    return <Icon type={ICON_TYPE.checkFilled} size={ICON_SIZE.medium} color={colors.green} />;
  }

  if (displayState === DISPLAY_STATE.ERROR) {
    return <Icon type={ICON_TYPE.circleWarning} size={ICON_SIZE.small} color={colors.error} />;
  }

  return <Spinner size="1.6rem" />;
};

const getStatusChip = (displayState) => {
  if (displayState === DISPLAY_STATE.READY) {
    return (
      <Status enabled>
        <FormattedMessage id="AIRoleBuilder.Tasks.Status.Ready" />
      </Status>
    );
  }

  if (displayState === DISPLAY_STATE.ERROR) {
    return (
      <Status enabled={false}>
        <FormattedMessage id="AIRoleBuilder.Tasks.Status.Failed" />
      </Status>
    );
  }

  return (
    <Status color={colors.purpleRainBase} background={colors.purpleRainLight3} dotColor={colors.purpleRainBase}>
      <FormattedMessage id="AIRoleBuilder.Tasks.Status.Building" />
    </Status>
  );
};

// The agent's current stage, stored verbatim so new stages need no change here.
const getStep = (task) => {
  const displayState = getRoleBuilderTaskDisplayState(task);

  if (displayState === DISPLAY_STATE.ERROR) {
    return task?.errorMessage || <FormattedMessage id="AIRoleBuilder.Error" />;
  }

  if (displayState === DISPLAY_STATE.READY) {
    return <FormattedMessage id="AIRoleBuilder.Tasks.Step.Ready" />;
  }

  return task?.agentStatus || <FormattedMessage id="AIRoleBuilder.Tasks.Step.Queued" />;
};

// "2 building · 1 ready · 1 failed", omitting whichever counts are zero.
const renderSummary = (tasks) => {
  const countOf = (displayState) =>
    tasks.filter((task) => getRoleBuilderTaskDisplayState(task) === displayState).length;

  return [
    { count: countOf(DISPLAY_STATE.PROCESSING), id: "AIRoleBuilder.Tasks.Summary.Building" },
    { count: countOf(DISPLAY_STATE.READY), id: "AIRoleBuilder.Tasks.Summary.Ready" },
    { count: countOf(DISPLAY_STATE.ERROR), id: "AIRoleBuilder.Tasks.Summary.Failed" },
  ]
    .filter(({ count }) => count > 0)
    .map(({ count, id }, index) => (
      <Fragment key={id}>
        {index > 0 && " · "}
        <FormattedMessage id={id} values={{ count }} />
      </Fragment>
    ));
};

const styles = {
  summary: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
  step: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
  requested_by: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyBase};
  `,
  actions: css`
    justify-content: flex-end;
    flex-wrap: nowrap;
  `,
  description: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
    word-break: break-word;
  `,
  file: css`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
};

AIRoleBuilderTasksList.propTypes = {
  onReview: PropTypes.func,
  showHeader: PropTypes.bool,
};

export default AIRoleBuilderTasksList;

// Consumed by useAIRoleBuilderTasks for the discard confirmation copy.
export { taskDisplayName, getStep };
